/**
 * British display formats for dates from the backend. Date-only values
 * (`YYYY-MM-DD`) are read as local calendar dates: `new Date('YYYY-MM-DD')`
 * would read UTC midnight and show the previous day west of UTC. Timestamps
 * are shown as the viewer's local calendar date.
 */

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const LONG_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const SHORT_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function parseDateOnly(isoDate: string): Date | null {
  const match = DATE_ONLY_PATTERN.exec(isoDate);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function formatDateOnly(format: Intl.DateTimeFormat, isoDate: string | null | undefined, empty: string): string {
  if (!isoDate) return empty;
  const date = parseDateOnly(isoDate);
  return date ? format.format(date) : isoDate;
}

/**
 * Format a `YYYY-MM-DD` date as "2 May 2026"; passes through bad input.
 * Missing input renders as `empty`.
 */
export function formatLongDate(isoDate: string | null | undefined, { empty = '—' }: { empty?: string } = {}): string {
  return formatDateOnly(LONG_DATE, isoDate, empty);
}

/**
 * Format a `YYYY-MM-DD` date as "2 Sept 2026"; passes through bad input.
 * Missing input renders as `empty`.
 */
export function formatShortDate(isoDate: string | null | undefined, { empty = '—' }: { empty?: string } = {}): string {
  return formatDateOnly(SHORT_DATE, isoDate, empty);
}

function formatTimestamp(format: Intl.DateTimeFormat, iso: string | null | undefined, empty: string): string {
  if (!iso) return empty;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : format.format(date);
}

/**
 * Format a timestamp as its local calendar date, "2 May 2026"; passes through bad input.
 * Missing input renders as `empty`.
 */
export function formatLongTimestamp(iso: string | null | undefined, { empty = '—' }: { empty?: string } = {}): string {
  return formatTimestamp(LONG_DATE, iso, empty);
}

/**
 * Format a timestamp as its local calendar date, "2 Sept 2026"; passes through bad input.
 * Missing input renders as `empty`.
 */
export function formatShortTimestamp(iso: string | null | undefined, { empty = '—' }: { empty?: string } = {}): string {
  return formatTimestamp(SHORT_DATE, iso, empty);
}

/** Format a UTC ISO string as British `dd/mm/yyyy`; passes through bad input. */
export function formatDateBritish(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC' }).format(d);
}
